import { FC, useCallback, useContext, useState } from "react";

import { Grid, Stack } from "@mui/material";
import { PublicRoutes } from "@simplyk/common";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";

import { MethodCard } from "./MethodCard/MethodCard";

import { RecaptchaAction } from "@/common/constants/recaptcha";
import { CurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useProtectByStrongAuth } from "@/common/hooks/useProtectByStrongAuth";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader";

interface MethodsSectionProps {
  onActivateTotp: () => void;
  onActivateSms: () => void;
  onEditPhoneNumber: (updatePhoneToken: string) => void;
}

export const MethodsSection: FC<MethodsSectionProps> = ({ onActivateTotp, onActivateSms, onEditPhoneNumber }) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, refetchUser } = useContext(CurrentUserContext);
  const { mutateWithSmsCode, mutateWithSmsCodeAndRecaptcha } = useProtectByStrongAuth("update mfa preferences");

  const { mutateAsync: deactivateTotp } = trpc.deactivateTotp.useMutation();
  const { mutateAsync: deactivateSmsMfa } = trpc.deactivateSmsMfa.useMutation();
  const { mutateAsync: updatePhoneNumberAskToken } = trpc.updatePhoneNumberAskToken.useMutation();

  const isTotpActivated = Boolean(currentUser?.isTotpMfaActivated);
  const isSmsMfaActivated = Boolean(currentUser?.isSmsMfaActivated);
  const maskedPhoneNumber = currentUser?.maskedPhoneNumber;

  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isEditPhoneLoading, setIsEditPhoneLoading] = useState(false);

  const handleError = (error: { code: string }) => {
    enqueueSnackbar(
      t(
        "dashboard",
        error.code === "authenticate_sms_otp_code_incorrect" || error.code === "authenticate_totp_code_incorrect"
          ? "stytch2FA.error.wrongCode"
          : "common.gqlError"
      ),
      { vibe: "danger" }
    );
    if (error.code === "session_jwt_not_found") {
      router.push(PublicRoutes.SignOut);
    }
  };

  const handleDeactivateTotp = async () => {
    setIsDeactivating(true);
    const response = await mutateWithSmsCode(deactivateTotp, {});
    if (response.error) {
      if (response.error.code === "cannot_deactivate_only_mfa_method") {
        enqueueSnackbar(t("dashboard", "twoFactor.errorCannotDeactivateOnlyMethod"), { vibe: "danger" });
      } else {
        handleError(response.error);
      }
    } else if (response.data) {
      enqueueSnackbar(t("dashboard", "twoFactor.totp.deactivateSuccess"), { vibe: "positive" });
      refetchUser();
    }
    setIsDeactivating(false);
  };

  const handleDeactivateSms = async () => {
    setIsDeactivating(true);
    const response = await mutateWithSmsCode(deactivateSmsMfa, {});
    if (response.error) {
      if (response.error.code === "cannot_deactivate_only_mfa_method") {
        enqueueSnackbar(t("dashboard", "twoFactor.errorCannotDeactivateOnlyMethod"), { vibe: "danger" });
      } else {
        handleError(response.error);
      }
    } else if (response.data) {
      enqueueSnackbar(t("dashboard", "twoFactor.sms.deactivateSuccess"), { vibe: "positive" });
      refetchUser();
    }
    setIsDeactivating(false);
  };

  const handleEditPhoneNumber = useCallback(async () => {
    setIsEditPhoneLoading(true);

    const res = await mutateWithSmsCodeAndRecaptcha(
      RecaptchaAction.UpdatePhoneNumberAskToken,
      currentUser?.email || "",
      updatePhoneNumberAskToken,
      {}
    );
    if (res.data) {
      onEditPhoneNumber(res.data);
    } else {
      enqueueSnackbar(t("dashboard", "common.updatePhoneNumberError"), { vibe: "danger" });
    }

    setIsEditPhoneLoading(false);
  }, [
    currentUser?.email,
    enqueueSnackbar,
    mutateWithSmsCodeAndRecaptcha,
    onEditPhoneNumber,
    t,
    updatePhoneNumberAskToken,
  ]);

  return (
    <Grid container spacing={1.5} sx={{ alignItems: "stretch" }}>
      {/* Authenticator App Card */}
      <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
        <MethodCard
          slots={{
            label: (
              <DashboardTypography vibe="text-brand-quiet" variant="caption">
                {t("dashboard", "twoFactor.recommended")}
              </DashboardTypography>
            ),
            title: t("dashboard", "twoFactor.totp.title"),
            description: t("dashboard", "twoFactor.totp.settingsDescription"),
            action: isTotpActivated ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <DashboardChip label="Active" vibe="positive" variant="outlined" data-test="chip-totp-active" />
                {isSmsMfaActivated && (
                  <DashboardLink onClick={handleDeactivateTotp} vibe="danger" pointer data-test="deactivate-totp-link">
                    {isDeactivating ? <DashboardZeffyLoader size="small" /> : t("dashboard", "twoFactor.deactivate")}
                  </DashboardLink>
                )}
              </Stack>
            ) : (
              <DashboardButton
                variant="tonal"
                vibe="brand"
                onClick={onActivateTotp}
                data-test="activate-totp-button"
                fullWidth
              >
                {t("dashboard", "twoFactor.totp.activate")}
              </DashboardButton>
            ),
          }}
        />
      </Grid>

      {/* SMS Card */}
      <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
        <MethodCard
          slots={{
            label: (
              <DashboardTypography vibe="text-positive-quiet" variant="caption">
                {t("dashboard", "twoFactor.easiest")}
              </DashboardTypography>
            ),
            title: t("dashboard", "twoFactor.sms.title"),
            description: t("dashboard", "twoFactor.sms.settingsDescription"),
            action: isSmsMfaActivated ? (
              <Stack spacing={1.5}>
                {maskedPhoneNumber && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <DashboardTypography variant="body2">{maskedPhoneNumber}</DashboardTypography>
                    <DashboardLink
                      onClick={handleEditPhoneNumber}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {t("dashboard", "settings.twoFactorAuthBySmsModal.general.description.editPhoneNumber")}
                      {isEditPhoneLoading ? <DashboardZeffyLoader size="small" /> : null}
                    </DashboardLink>
                  </Stack>
                )}
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <DashboardChip label="Active" vibe="positive" variant="outlined" data-test="chip-sms-active" />
                  {isTotpActivated && (
                    <DashboardLink onClick={handleDeactivateSms} vibe="danger" pointer data-test="deactivate-sms-link">
                      {isDeactivating ? <DashboardZeffyLoader size="small" /> : t("dashboard", "twoFactor.deactivate")}
                    </DashboardLink>
                  )}
                </Stack>
              </Stack>
            ) : (
              <DashboardButton
                variant="tonal"
                vibe="brand"
                onClick={onActivateSms}
                data-test="activate-sms-button"
                fullWidth
              >
                {t("dashboard", "twoFactor.sms.activate")}
              </DashboardButton>
            ),
          }}
        />
      </Grid>
    </Grid>
  );
};
